import { Component, OnInit, OnDestroy, NgZone, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { SipService } from '../../services/sip.service';
import { VoipWorkstationService } from '../../services/voip-workstation.service';
import { UserService } from '../../../shared/services/user.service';
import { SocketService } from '../../../shared/services/socket.service';
import { CallEndReason, CallInfo } from '../../models/voip.models';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { ListState, listStateOf } from '../../../shared/components/list-state/list-state.component';
import { withUtcZone } from '../../../shared/alarms-api';

// Jak w socket.service.ts — o środowisku decyduje adres API, nie flaga `production`.
// Logi poniżej budowały tablice (filter/map po całym zespole) przy każdej zmianie
// statusu, czyli wykonywały realną pracę wyłącznie po to, żeby coś wypisać.
const DEBUG = environment.api_url.includes('sidly-platform-dev');

const END_REASON_KEYS: { [reason in CallEndReason]: string } = {
    operator_rejected: 'VOIP.END_REASON_OPERATOR_REJECTED',
    caller_hung_up: 'VOIP.END_REASON_CALLER_HUNG_UP',
    caller_hung_up_waiting: 'VOIP.END_REASON_CALLER_HUNG_UP_WAITING',
    operator_cancelled: 'VOIP.END_REASON_OPERATOR_CANCELLED',
    busy: 'VOIP.END_REASON_BUSY',
    callee_rejected: 'VOIP.END_REASON_CALLEE_REJECTED',
    no_answer: 'VOIP.END_REASON_NO_ANSWER',
    unreachable: 'VOIP.END_REASON_UNREACHABLE',
    failed: 'VOIP.END_REASON_FAILED'
};

interface TeamMember {
    id: number;
    name: string;
    avatar: boolean;
    status: 'online' | 'break' | 'on_call' | 'offline';
    type: string;
    workstationName: string;
    sessionDuration: number;
    // Status z REST /team-status (sesja STANOWISKA VOIP: online/on_call/break) —
    // panel jest VOIP-owy, więc TYLKO to decyduje o "Dostępny"/"Offline".
    wsStatus?: 'online' | 'break' | 'on_call' | 'offline';
    // Obecność na PLATFORMIE (socket/token z node) — śledzone OSOBNO, pokazywane
    // jako drugorzędny wskaźnik; nie wpływa na status VOIP ani kolejność.
    platformOnline?: boolean;
}

@Component({
    selector: 'app-voip-sidebar',
    templateUrl: './voip-sidebar.component.html',
    styleUrls: ['./voip-sidebar.component.scss']
})
export class VoipSidebarComponent implements OnInit, OnDestroy {

    isVisible: boolean = false;
    isCollapsed: boolean = localStorage.getItem('voipSidebarCollapsed') === 'true';
    isSmallScreen: boolean = false;
    private readonly SMALL_BREAKPOINT = 991;

    // Sekcja 1: Aktualna rozmowa
    callInfo: CallInfo = {
        state: 'idle',
        direction: null,
        remoteNumber: null,
        startTime: null,
        duration: 0
    };

    // Sekcja 2: Kolejka (uproszczona)
    pendingCalls: any[] = [];

    // Sekcja 3: Historia połączeń
    callHistory: any[] = [];
    /**
     * Czy pierwsze pobranie historii już wróciło. Bez tej flagi pusta tablica w trakcie
     * pobierania jest nie do odróżnienia od „nie było żadnych połączeń", a sekcja po
     * prostu nie istnieje — panel wygląda, jakby jej nigdy nie było.
     */
    callHistoryLoading = true;

    // Sekcja 4: Zespół
    teamMembers: TeamMember[] = [];
    /** Jak wyżej, dla listy zespołu. */
    teamLoading = true;
    // ID ratowników online wg socketu (event 'statusOfCaregivers') — to jest
    // wiarygodne źródło obecności; status z /team-status bywał zawsze 'offline'.
    private onlineCaregiverIds = new Set<number>();

    private subscriptions: Subscription[] = [];

    constructor(
        private sipService: SipService,
        private workstationService: VoipWorkstationService,
        private userService: UserService,
        private socketService: SocketService,
        private ngZone: NgZone,
        private translateService: TranslateService
    ) {}

    ngOnInit(): void {
        this.checkScreenSize();
        this.checkVisibility();
    }

    @HostListener('window:resize')
    onResize(): void {
        this.checkScreenSize();
    }

    private checkScreenSize(): void {
        const wasSmall = this.isSmallScreen;
        this.isSmallScreen = window.innerWidth <= this.SMALL_BREAKPOINT;

        // Auto-collapse when entering small screen
        if (this.isSmallScreen && !wasSmall) {
            this.isCollapsed = true;
        }
        this.updateBodyClass();
    }

    private checkVisibility(): void {
        this.subscriptions.push(
            this.userService.currentUser.pipe(
                filter(user => user && Object.keys(user).length > 0),
                take(1)
            ).subscribe(user => {
                const isManager = user.role === 'ROLE_API_MANAGER';
                const isParamedic = user.role === 'ROLE_API_CAREGIVER' && user.user_id && user.user_id.type === 'Paramedic';

                if (isManager || isParamedic) {
                    this.isVisible = true;
                    this.updateBodyClass();
                    this.setupSubscriptions();
                    this.loadTeamStatus();
                    this.loadRecentCalls();
                }
            })
        );
    }

    private setupSubscriptions(): void {
        // Subskrybuj stan aktualnego połączenia
        this.subscriptions.push(
            this.sipService.getCallInfo().subscribe(info => {
                this.callInfo = info;
            })
        );

        if (DEBUG) { console.log('[VoipSidebar] setupSubscriptions: subskrybuję workstationChange / callStateChange / statusOfCaregivers'); }

        // Subskrybuj zmiany statusu stanowisk (Socket.IO)
        this.subscriptions.push(
            this.socketService.onWorkstationChange().subscribe(data => {
                if (DEBUG) { console.log('[VoipSidebar] workstationChange otrzymany:', data); }
                this.ngZone.run(() => this.handleWorkstationChange(data));
            })
        );

        // Subskrybuj zmiany stanu połączeń (Socket.IO)
        this.subscriptions.push(
            this.socketService.onCallStateChange().subscribe(data => {
                this.ngZone.run(() => this.handleCallStateChange(data));
            })
        );

        // Obecność zespołu z socketu: lista ID ratowników online
        this.subscriptions.push(
            this.socketService.onStatus().subscribe(ids => {
                this.ngZone.run(() => {
                    this.onlineCaregiverIds = new Set((ids || []).map((x: any) => Number(x)));
                    if (DEBUG) {
                        console.log('[VoipSidebar] statusOfCaregivers: online IDs =', Array.from(this.onlineCaregiverIds).slice(0, 30));
                    }
                    this.applyOnlinePresence();
                });
            })
        );

        // Subskrybuj zakończenie rozmów — dodaj do lokalnej historii
        this.subscriptions.push(
            this.sipService.getCallEnded().subscribe(call => {
                this.ngZone.run(() => {
                    const entry = {
                        direction: call.direction,
                        phoneNumber: call.remoteNumber,
                        duration: call.duration,
                        startedAt: call.startTime ? call.startTime.toISOString() : null,
                        status: call.duration > 0 ? 'answered' : 'missed',
                        endReason: call.endReason
                    };
                    this.callHistory.unshift(entry);
                    // Zachowaj max 10
                    if (this.callHistory.length > 10) {
                        this.callHistory = this.callHistory.slice(0, 10);
                    }
                    this.fillCallerNames([entry]);
                });
            })
        );
    }

    /**
     * Stany obu list panelu w kontrakcie `ListStateComponent`. `isEmpty` zostaje `false`:
     * pustkę opisuje sam panel — sekcja historii po prostu znika, gdy nie ma połączeń,
     * a lista zespołu jest wtedy pusta. Wspólne „Brak danych" nic by tu nie dodało.
     */
    get recentCallsState(): ListState {
        return listStateOf(this.callHistoryLoading, this.callHistory, { ownEmpty: true });
    }

    get teamState(): ListState {
        return listStateOf(this.teamLoading, this.teamMembers, { ownEmpty: true });
    }

    private loadTeamStatus(): void {
        this.workstationService.getTeamStatus().subscribe(
            response => {
                if (response.team) {
                    this.teamMembers = response.team.map((m: TeamMember) => ({ ...m, wsStatus: m.status }));
                    if (DEBUG) {
                        console.log('[VoipSidebar] team-status (REST):',
                            this.teamMembers.map(m => `${m.id}:${m.name}=${m.wsStatus}` + (m.workstationName ? `@${m.workstationName}` : '')));
                    }
                    this.applyOnlinePresence();
                }
                this.teamLoading = false;
            },
            error => {
                this.teamLoading = false;
                console.error('VoipSidebar - Error loading team status:', error);
            }
        );
    }

    private loadRecentCalls(): void {
        this.workstationService.getRecentCalls(10).subscribe(
            response => {
                if (response && response.data) {
                    this.callHistory = response.data;
                    this.fillCallerNames(this.callHistory);
                }
                this.callHistoryLoading = false;
            },
            error => {
                this.callHistoryLoading = false;
                console.error('VoipSidebar - Error loading recent calls:', error);
            }
        );
    }

    private fillCallerNames(calls: any[]): void {
        new Set<string>(calls.map(call => call.phoneNumber).filter(Boolean)).forEach(phone =>
            this.workstationService.findPatientByPhone(phone).subscribe(
                response => {
                    if (!response.found) return;
                    const name = `${response.patient.firstName} ${response.patient.lastName}`;
                    this.callHistory.filter(call => call.phoneNumber === phone).forEach(call => call.callerName = name);
                },
                error => console.error('VoipSidebar - Error finding patient by phone:', phone, error)
            )
        );
    }

    private callTimeLabels = new Map<string, string>();
    private callTimeBucket = '';

    getCallTimeLabel(startedAt: string): string {
        if (!startedAt) return '';

        const bucket = Math.floor(Date.now() / 60000) + '|' + this.translateService.currentLang;
        if (this.callTimeBucket !== bucket) {
            this.callTimeBucket = bucket;
            this.callTimeLabels.clear();
        }

        const cached = this.callTimeLabels.get(startedAt);
        if (cached !== undefined) return cached;

        const val = this.buildCallTimeLabel(startedAt);
        this.callTimeLabels.set(startedAt, val);

        return val;
    }

    private buildCallTimeLabel(startedAt: string): string {
        // started_at z backendu jest w UTC bez strefy — bez 'Z' wychodziło „2h temu" dla świeżej rozmowy
        const date = new Date(withUtcZone(startedAt));
        if (isNaN(date.getTime())) return startedAt;
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return this.translateService.instant('VOIP.TIME_NOW');
        if (diffMins < 60) return this.translateService.instant('VOIP.TIME_MINS_AGO', { mins: diffMins });
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return this.translateService.instant('VOIP.TIME_HOURS_AGO', { hours: diffHours });
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const mi = String(date.getMinutes()).padStart(2, '0');
        return `${dd}.${mm} ${hh}:${mi}`;
    }

    private handleWorkstationChange(data: any): void {
        // Odśwież status zespołu po zmianach (login/logout/break)
        this.loadTeamStatus();
    }

    private handleCallStateChange(data: any): void {
        // Aktualizuj status konkretnego ratownika
        const member = this.teamMembers.find(m => m.id === data.caregiverId);
        if (member) {
            member.status = data.action === 'call_start' ? 'on_call' : 'online';
            member.wsStatus = member.status;
            this.sortTeam();
        }
    }

    // Panel jest VOIP-owy, więc dwa źródła obecności są śledzone ROZDZIELNIE:
    //  1) status = WYŁĄCZNIE sesja stanowiska VOIP (REST /team-status + eventy);
    //     wylogowanie ze stanowiska = Offline, nawet gdy platforma wciąż otwarta
    //     (wcześniej socket podbijał z powrotem na "Dostępny" — zgłoszenie Łukasza),
    //  2) platformOnline = obecność na platformie/mobilce (socket statusOfCaregivers),
    //     pokazywana osobną kropką, bez wpływu na status i kolejność.
    private applyOnlinePresence(): void {
        this.teamMembers.forEach(m => {
            m.status = m.wsStatus || 'offline';
            m.platformOnline = this.onlineCaregiverIds.has(Number(m.id));
        });
        this.sortTeam();
        if (DEBUG) {
            console.log('[VoipSidebar] statusy (VOIP | platforma):',
                this.teamMembers
                    .filter(m => m.status !== 'offline' || m.platformOnline)
                    .map(m => `${m.id}:${m.name} voip=${m.status} platforma=${m.platformOnline ? 'tak' : 'nie'}`),
                '| reszta offline:', this.teamMembers.filter(m => m.status === 'offline' && !m.platformOnline).length);
        }
    }

    // Deterministyczna kolejność: rozmowa > dostępny > przerwa > (offline z platformą) > offline,
    // w obrębie grupy alfabetycznie — bez tego pozycje "skakały" po każdym evencie.
    private sortTeam(): void {
        const order: { [key: string]: number } = { on_call: 0, online: 1, break: 2, offline: 3 };
        this.teamMembers.sort((a, b) => {
            const oa = order[a.status] !== undefined ? order[a.status] : 9;
            const ob = order[b.status] !== undefined ? order[b.status] : 9;
            if (oa !== ob) return oa - ob;
            const pa = a.platformOnline ? 0 : 1;
            const pb = b.platformOnline ? 0 : 1;
            if (pa !== pb) return pa - pb;
            return (a.name || '').localeCompare(b.name || '');
        });
    }

    toggleCollapse(): void {
        this.isCollapsed = !this.isCollapsed;
        localStorage.setItem('voipSidebarCollapsed', String(this.isCollapsed));
        this.updateBodyClass();
    }

    private updateBodyClass(): void {
        if (this.isVisible && !this.isCollapsed && !this.isSmallScreen) {
            document.body.classList.add('voip-sidebar-open');
        } else {
            document.body.classList.remove('voip-sidebar-open');
        }
    }

    onBackdropClick(): void {
        this.isCollapsed = true;
    }

    getStatusLabel(status: string): string {
        switch (status) {
            case 'online': return this.translateService.instant('VOIP.CAREGIVER_AVAILABLE');
            case 'break': return this.translateService.instant('VOIP.CAREGIVER_ON_BREAK');
            case 'on_call': return this.translateService.instant('VOIP.CAREGIVER_ON_CALL');
            case 'offline': return this.translateService.instant('VOIP.CAREGIVER_OFFLINE');
            default: return status;
        }
    }

    getStatusClass(status: string): string {
        return 'status-' + status;
    }

    getEndReasonLabel(reason: string | null): string {
        return this.translateService.instant(END_REASON_KEYS[reason as CallEndReason] || 'VOIP.END_REASON_UNKNOWN');
    }

    formatDuration(seconds: number): string {
        if (!seconds) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        document.body.classList.remove('voip-sidebar-open');
    }
}
